/** @format */

import type { AxiosError } from "axios";
import type { FieldErrors, Path, UseFormSetError } from "react-hook-form";

import { isHttpError, isNetworkError, isTimeoutError } from "@/api/galaxieClient";

/**
 * Utility functions for handling form errors consistently across the application.
 */

export interface ApiErrorResponse {
  [key: string]: string | string[] | ApiErrorResponse | ApiErrorResponse[] | undefined;
  nonFieldErrors?: string | string[];
}

/**
 * Extracts error messages from API error responses.
 * Handles both Django REST Framework and custom API error formats.
 * Also handles new error types (NetworkError, TimeoutError, HttpError).
 */
export function extractApiErrorMessage(
  error: unknown,
  fieldName?: string
): string | undefined {
  // Handle new error types first
  if (isNetworkError(error)) {
    return "Unable to connect to server. Please check your internet connection and try again.";
  }

  if (isTimeoutError(error)) {
    return `Request timed out after ${error.timeout}ms. The server may be slow. Please try again.`;
  }

  if (isHttpError(error)) {
    // Handle field-specific errors for HTTP errors
    if (fieldName) {
      const errorData = error.data as ApiErrorResponse | undefined;
      if (errorData?.[fieldName]) {
        const fieldError = errorData[fieldName];
        if (typeof fieldError === "string") {
          return fieldError;
        }
        if (Array.isArray(fieldError)) {
          return fieldError
            .filter((e): e is string => typeof e === "string")
            .join("\n");
        }
      }
    }

    // Handle non-field errors
    const errorData = error.data as ApiErrorResponse | undefined;
    if (errorData?.nonFieldErrors) {
      const nonFieldError = errorData.nonFieldErrors;
      if (typeof nonFieldError === "string") {
        return nonFieldError;
      }
      if (Array.isArray(nonFieldError)) {
        return nonFieldError
          .filter((e): e is string => typeof e === "string")
          .join("\n");
      }
    }

    // Handle generic error messages
    if (errorData?.error) {
      const errorMessage = errorData.error;
      if (typeof errorMessage === "string") {
        return errorMessage;
      }
    }

    // Fallback to status-based messages for HTTP errors
    switch (error.status) {
      case 400:
        return "Invalid data provided";
      case 401:
        return "Authentication required";
      case 403:
        return "You don't have permission to perform this action";
      case 404:
        return "Resource not found";
      case 500:
        return "Server error. Please try again later";
      default:
        return error.statusText ?? "An unexpected error occurred";
    }
  }

  // Fallback to handling as AxiosError for backward compatibility
  if (!(error instanceof Error)) {
    return "An unexpected error occurred";
  }

  const axiosError = error as AxiosError<ApiErrorResponse>;
  const response = axiosError.response;

  if (!response) {
    return error.message ?? "An unexpected error occurred";
  }

  // Handle field-specific errors
  if (fieldName && response.data?.[fieldName]) {
    const fieldError = response.data[fieldName];
    if (typeof fieldError === "string") {
      return fieldError;
    }
    if (Array.isArray(fieldError)) {
      return fieldError.filter((e): e is string => typeof e === "string").join("\n");
    }
  }

  // Handle non-field errors
  if (response.data?.nonFieldErrors) {
    const nonFieldError = response.data.nonFieldErrors;
    if (typeof nonFieldError === "string") {
      return nonFieldError;
    }
    if (Array.isArray(nonFieldError)) {
      return nonFieldError.join("\n");
    }
  }

  // Handle generic error messages
  if (response.data?.error) {
    const errorData = response.data.error;
    if (typeof errorData === "string") {
      return errorData;
    }
  }

  // Fallback to status-based messages
  switch (response.status) {
    case 400:
      return "Invalid data provided";
    case 401:
      return "Authentication required";
    case 403:
      return "You don't have permission to perform this action";
    case 404:
      return "Resource not found";
    case 500:
      return "Server error. Please try again later";
    default:
      return error.message ?? "An unexpected error occurred";
  }
}

/**
 * Sets form errors from an API error response.
 * Automatically maps field names and handles nested errors.
 * Handles new error types (NetworkError, TimeoutError, HttpError).
 */
export function setFormErrorsFromApi<T extends Record<string, unknown>>(
  error: unknown,
  setError: UseFormSetError<T>,
  fieldMapping?: Record<string, keyof T>
): void {
  // Handle new error types
  if (isNetworkError(error) || isTimeoutError(error)) {
    // Network and timeout errors are typically not field-specific
    const message = extractApiErrorMessage(error);
    setError("root" as Path<T>, {
      type: "manual",
      message,
    });
    return;
  }

  if (isHttpError(error)) {
    // Handle HTTP errors with field-specific error data
    const errorData = error.data as ApiErrorResponse | undefined;

    if (!errorData) {
      // Set a generic error if no specific field errors
      const message = extractApiErrorMessage(error);
      setError("root" as Path<T>, {
        type: "manual",
        message,
      });
      return;
    }

    // Handle field-specific errors
    Object.keys(errorData).forEach(key => {
      if (key === "nonFieldErrors" || key === "error") {
        return; // Handle these separately
      }

      const fieldName = (fieldMapping?.[key] ?? key) as Path<T>;
      const fieldError = errorData[key];

      if (typeof fieldError === "string") {
        setError(fieldName, {
          type: "manual",
          message: fieldError,
        });
      } else if (Array.isArray(fieldError)) {
        const message = fieldError
          .filter((e): e is string => typeof e === "string")
          .join("\n");
        setError(fieldName, {
          type: "manual",
          message,
        });
      }
    });

    // Handle non-field errors - set on the first field or root
    if (errorData.nonFieldErrors) {
      const nonFieldError = errorData.nonFieldErrors;
      const message = Array.isArray(nonFieldError)
        ? nonFieldError.filter((e): e is string => typeof e === "string").join("\n")
        : typeof nonFieldError === "string"
          ? nonFieldError
          : undefined;

      // Try to set on a common field like "email" or "password", otherwise use root
      const commonFields: Path<T>[] = [
        "email",
        "password",
        "email1",
        "password1",
      ] as Path<T>[];
      const targetField =
        commonFields.find(field => field in errorData) ?? ("root" as Path<T>);

      setError(targetField, {
        type: "manual",
        message,
      });
    }
    return;
  }

  // Fallback to handling as AxiosError for backward compatibility
  const axiosError = error as AxiosError<ApiErrorResponse>;
  const response = axiosError.response;

  if (!response?.data) {
    // Set a generic error if no specific field errors
    const message = extractApiErrorMessage(error);
    setError("root" as Path<T>, {
      type: "manual",
      message,
    });
    return;
  }

  const errorData = response.data;

  // Handle field-specific errors
  Object.keys(errorData).forEach(key => {
    if (key === "nonFieldErrors" || key === "error") {
      return; // Handle these separately
    }

    const fieldName = (fieldMapping?.[key] ?? key) as Path<T>;
    const fieldError = errorData[key];

    if (typeof fieldError === "string") {
      setError(fieldName, {
        type: "manual",
        message: fieldError,
      });
    } else if (Array.isArray(fieldError)) {
      const message = fieldError
        .filter((e): e is string => typeof e === "string")
        .join("\n");
      setError(fieldName, {
        type: "manual",
        message,
      });
    }
  });

  // Handle non-field errors - set on the first field or root
  if (errorData.nonFieldErrors) {
    const nonFieldError = errorData.nonFieldErrors;
    const message = Array.isArray(nonFieldError)
      ? nonFieldError.filter((e): e is string => typeof e === "string").join("\n")
      : typeof nonFieldError === "string"
        ? nonFieldError
        : undefined;

    // Try to set on a common field like "email" or "password", otherwise use root
    const commonFields: Path<T>[] = [
      "email",
      "password",
      "email1",
      "password1",
    ] as Path<T>[];
    const targetField =
      commonFields.find(field => field in errorData) ?? ("root" as Path<T>);

    setError(targetField, {
      type: "manual",
      message,
    });
  }
}

/**
 * Gets the first error message from form errors.
 * Useful for displaying a single error message at the top of a form.
 */
export function getFirstFormError<T extends Record<string, unknown>>(
  errors: FieldErrors<T>
): string | undefined {
  const errorEntries = Object.entries(errors);
  if (errorEntries.length === 0) {
    return undefined;
  }

  const firstError = errorEntries[0];
  const message = firstError[1]?.message;
  if (typeof message === "string") {
    return message;
  }

  return undefined;
}

/**
 * Checks if a form has any errors.
 */
export function hasFormErrors<T extends Record<string, unknown>>(
  errors: FieldErrors<T>
): boolean {
  return Object.keys(errors).length > 0;
}
